import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { User } from '@api/models/user.model';
import { EmployeeService } from '@api/services/employee.service';
import { AuthService } from '@auth/services/auth.service';
import { ButtonComponent } from '@ui/button/button.component';
import { InputComponent } from '@ui/input/input.component';
import { ModalComponent } from '@ui/modal/modal.component';
import { MODAL_REF } from '@ui/modal/modal.service';
import { ModalService } from '@ui/modal/modal.service';
import { ModalRef } from '@ui/modal/modal.service';
import { NotificationService } from '@ui/notification/notification.service';
import { AppService } from '../../../app.service';
import { ConfirmService } from '@ui/confirm-modal/confirm.service';

@Component({
  selector: 'app-employees-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent, InputComponent, ReactiveFormsModule, ButtonComponent],
  template: ` <ui-modal
    [title]="employee ? 'Medewerker aanpassen' : 'Medewerker uitnodigen'"
    [submitText]="'Opslaan'"
    [cancelText]="'Annuleren'"
    (close)="closeModal()"
    (save)="saveModal()"
  >
    <form [formGroup]="form">
      <div class="flex flex-col gap-4">
        <ui-input
          id="name"
          label="Naam"
          placeholder="Naam"
          formControlName="name"
          [required]="true"
        />
        <ui-input
          id="email"
          label="Email"
          placeholder="Email"
          formControlName="email"
          [required]="true"
          [autocomplete]="'off'"
        />
        <!-- <ng-container *ngIf="!employee?.id">
          <ui-input
            id="password"
            label="Wachtwoord"
            placeholder="Wachtwoord"
            formControlName="password"
            [required]="true"
            [type]="'password'"
            [autocomplete]="'new-password'"
          />
        </ng-container> -->

        <ui-input
          type="checkbox"
          id="is_consulent"
          label="Consulent"
          formControlName="is_consulent"
        />
        @if(this.authService.currentUser()?.is_admin || this.appService.isKeyUser()) {
        <ui-input
            type="checkbox"
            id="is_key_user"
            label="Key user"
            formControlName="is_key_user"
          />
        }

        @if(this.employee?.verified_at) {
          <p class="text-sm text-gray-500">Email geverifieerd op {{ this.employee?.verified_at | date:'dd-MM-yyyy HH:mm' }}</p>
        } @else if(this.employee?.id) {
          <ui-button
            [variant]="'secondary'"
            (click)="resendInvite()"
          >
            Uitnodiging opnieuw versturen
          </ui-button>
        }
        @if(this.employee?.two_factor_enabled) {
          <ui-button
            [variant]="'secondary'"
            (click)="reset2FA()"
          >
            Reset 2FA
          </ui-button>
        }
      </div>
    </form>
  </ui-modal>`,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmployeesModalComponent {
  employeeService = inject(EmployeeService);
  notificationService = inject(NotificationService);
  authService = inject(AuthService);
  appService = inject(AppService);
  confirmService = inject(ConfirmService);

  private modalRef = inject(MODAL_REF);
  modalService = inject(ModalService);

  employee: User | null = null;
  form: FormGroup = new FormGroup({});

  ngOnInit() {
    this.form = new FormGroup({
      name: new FormControl(this.employee?.name, Validators.required),
      email: new FormControl(this.employee?.email, [Validators.required, Validators.email]),
      is_consulent: new FormControl(this.employee?.is_consulent || false),
      is_admin: new FormControl(this.employee?.is_admin || false),
      is_key_user: new FormControl(this.employee?.is_key_user || false),
    });
  }

  closeModal() {
    this.modalService.close(this.modalRef as ModalRef<any>);
  }

  saveModal() {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.notificationService.error('Er zijn fouten in het formulier');
      return;
    }

    if (this.employee?.id) {
      this.employeeService.updateEmployee(this.employee.id, this.form.value).subscribe({
        next: (employee) => {
          this.notificationService.success('Medewerker succesvol aangepast');
          this.modalService.close(this.modalRef as ModalRef<any>);
        },
        error: (error) => {
          const errorMessage = error.error.message ?? 'Er is iets fout gegaan bij het aanpassen van de medewerker';
          this.notificationService.error(errorMessage);
        }
      });
    } else {
      this.employeeService.createEmployee(this.form.value).subscribe({
        next: (employee) => {
          this.notificationService.success('Medewerker succesvol aangemaakt');
          this.modalService.close(this.modalRef as ModalRef<any>);
        },
        error: (error) => {
          const errorMessage = error.error.message ?? 'Er is iets fout gegaan bij het aanmaken van de medewerker';
          this.notificationService.error(errorMessage);
        }
      });
    }
  }

  resendInvite() {
    if (!this.employee?.id) {
      return;
    }

    this.employeeService.resendInvite(this.employee?.id).subscribe({
      next: () => {
        this.notificationService.success('Uitnodiging succesvol verstuurd');
      },
      error: (error) => {
        this.notificationService.error('Er is iets fout gegaan bij het versturen van de uitnodiging');
      }
    });
  }
  reset2FA() {
    if (!this.employee?.id) {
      return;
    }

    this.confirmService.confirm({
      title: '2FA reset',
      message: 'Weet u zeker dat u de twee-factor authenticatie voor deze gebruiker wilt resetten?',
      confirmText: 'Ja, reset 2FA',
      cancelText: 'Annuleren'
    }).then((confirmed) => {
      if (confirmed) {
        this.employeeService.reset2FA(this.employee!.id).subscribe({
          next: () => {
            this.notificationService.success('2FA succesvol gereset');
            this.employee!.two_factor_enabled = false;
          },
          error: (error) => {
            this.notificationService.error('Er is iets fout gegaan bij het resetten van 2FA');
          }
        });
      }
    });
  }
}
